import { JiraSideNavMessage } from '@post-office/components--jira-side-nav-message';

import { useMessageContext } from '@atlassian/post-office-context/message-context';

import { useMessageInstanceEvent } from '@atlassian/post-office-context/message-lifecycle';

import { Body, productNameByVariant } from './Body';

import { registeredContentByProduct } from './content';

import { RequestSentFlag } from './RequestSentFlag';

import type { AdditionalProps, HydrationResult, TargetProduct } from '../types';

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useRequestAccess } from '../useRequestAccess';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { FormattedMessage } from 'react-intl';

export type Props = HydrationResult &
	AdditionalProps & {
		targetProduct: TargetProduct;
	};

/**
 * 'flag-only' hides the nudge while the request-sent confirmation is still on screen.
 * 'flag-dismissed' keeps the empty FlagGroup mounted so the flag can animate out; unmounting it
 * with the flag would destroy the ExitingPersistence before the exit runs.
 */
type Phase = 'visible' | 'flag-only' | 'flag-dismissed' | 'done';

export const JiraSideNavCrossJoinMessage = ({
	targetProduct,
	collaborators,
	onDismissed,
	tenantId,
	userId,
	sourceProduct,
	crossJoinUrl,
}: Props): ReactNode => {
	const content = registeredContentByProduct[targetProduct].default;
	const isFirstRender = useRef<boolean>(true);
	const [phase, setPhase] = useState<Phase>('visible');
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceViewed } = useMessageInstanceEvent();

	const { messageTemplateId } = useMessageContext();
	const requestAccess = useRequestAccess({
		targetProduct,
		tenantId,
		userId,
		sourceProduct,
		collaborators,
		crossJoinUrl,
		messageTemplateId,
		// Always fires, so keep the flag up if it was raised earlier in the same handler.
		onComplete: () => setPhase((current) => (current === 'flag-only' ? current : 'done')),
		showRequestSentFlag: () => setPhase('flag-only'),
	});

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			void messageInstanceViewed();
			void fireAnalyticsEvent({
				name: 'cjnNav4 placement',
				eventType: 'screen',
				attributes: {
					messageTemplateId,
					collaboratorCount: collaborators.length,
				},
			});
		}
	}, []);

	const attributes = useMemo(
		() => ({
			...(messageTemplateId ? { messageTemplateId } : {}),
		}),
		[messageTemplateId],
	);

	if (phase === 'done') {
		return null;
	}

	// Kept mounted across phases so the flag animates in rather than appearing with its group.
	const requestSentFlag = (
		<RequestSentFlag
			isVisible={phase === 'flag-only'}
			targetProduct={targetProduct}
			onDismiss={() => setPhase('flag-dismissed')}
		/>
	);

	if (phase === 'flag-only' || phase === 'flag-dismissed') {
		return requestSentFlag;
	}

	return (
		<>
			{requestSentFlag}
			<JiraSideNavMessage
				onDismissed={onDismissed}
				additionalAnalyticsAttributes={attributes}
				bodyContent={
					<Body
						sourceProduct={sourceProduct}
						targetProduct={targetProduct}
						collaborators={collaborators}
						tenantId={tenantId}
						userId={userId}
						crossJoinUrl={crossJoinUrl}
						messageTemplateId={messageTemplateId}
						onRequestAccess={requestAccess}
					/>
				}
				whyAmISeeingThisModalContent={{
					message: (
						<p>
							<FormattedMessage
								{...content.reasonsModalMessage}
								values={{
									productName: <strong>{productNameByVariant[targetProduct]}</strong>,
								}}
							/>
						</p>
					),
					reasons: [
						<FormattedMessage {...content.reasonTeamSize} />,
						<FormattedMessage {...content.reasonExistingProducts} />,
					],
				}}
			/>
		</>
	);
};
